/**
 * shared/video.js: thư viện video ngắn (YouTube Shorts) nhúng trong site.
 *
 * Mỗi video là một bản ghi nhỏ, mã YouTube làm khoá:
 *   { id, tieuDe, moTa, ngayDang, thoiLuong (giây), loat, ganVao: [đường dẫn], loiThoai, status }
 * `ganVao` là các trang trên site mà video giải thích (ví dụ /thuat-ngu/vesting-la-gi).
 * Trang đó hiện khối "Xem nhanh" và khai VideoObject cho Google.
 *
 * Dữ liệu có hai nguồn, giống quỹ và mẫu: danh sách trong code
 * (src/data/videos.js) và Firestore `videos` (sửa ở /admin/video). Bản trên
 * Firestore thay trọn bản trong code cùng mã. Hàm thuần, dùng ở cả trình
 * duyệt, prerender và test.
 */

export const TRANG_THAI_VIDEO = ['DRAFT', 'PUBLISHED'];
export const LOAI_TRANG = {
  'tu-dien': 'Từ điển',
  playbook: 'Playbook',
  'cong-cu': 'Công cụ',
  khac: 'Trang khác',
};

const MA = /^[A-Za-z0-9_-]{11}$/;

/** Lấy mã 11 ký tự từ link YouTube (shorts, watch, youtu.be, embed) hoặc chính mã. */
export function tachMaYoutube(s) {
  const t = String(s || '').trim();
  if (MA.test(t)) return t;
  const m = t.match(/(?:youtube(?:-nocookie)?\.com\/(?:shorts\/|embed\/|live\/|watch\?(?:[^#]*&)?v=)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : '';
}

/** Đường dẫn trên site: bỏ tên miền, dấu / cuối, tham số. */
export function chuanHoaDuongDan(p) {
  let s = String(p || '').trim();
  if (!s) return '';
  s = s.replace(/^https?:\/\/(www\.)?fundraising\.vn/i, '').replace(/[?#].*$/, '');
  if (!s.startsWith('/')) s = `/${s}`;
  return s === '/' ? '/' : s.replace(/\/+$/, '');
}

const chu = (v, max) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, max);

export function chuanHoaVideo(raw = {}) {
  const sec = Math.round(Number(raw.thoiLuong));
  const ngay = /^\d{4}-\d{2}-\d{2}$/.test(String(raw.ngayDang || '')) ? raw.ngayDang : '';
  return {
    id: tachMaYoutube(raw.id || raw.link || ''),
    tieuDe: chu(raw.tieuDe, 140),
    moTa: chu(raw.moTa, 400),
    ngayDang: ngay,
    thoiLuong: Number.isFinite(sec) && sec > 0 && sec <= 3600 ? sec : null,
    loat: chu(raw.loat, 60),
    ganVao: [...new Set((Array.isArray(raw.ganVao) ? raw.ganVao : String(raw.ganVao || '').split(/[\n,]+/))
      .map(chuanHoaDuongDan).filter((x) => x && x !== '/'))].slice(0, 6),
    loiThoai: String(raw.loiThoai ?? '').trim().slice(0, 4000),
    status: TRANG_THAI_VIDEO.includes(raw.status) ? raw.status : 'DRAFT',
  };
}

/** Lỗi cần sửa trước khi lưu. `coTrang(path)` (tuỳ chọn) kiểm đường dẫn có thật. */
export function kiemTraVideo(v, coTrang = null) {
  const loi = [];
  if (!v.id) loi.push('Dán link YouTube (dạng youtube.com/shorts/...) hoặc mã 11 ký tự.');
  if (!v.tieuDe) loi.push('Cần tiêu đề.');
  if (v.status === 'PUBLISHED' && !v.ngayDang) loi.push('Cần ngày đăng (Google bắt buộc với video).');
  if (coTrang) for (const p of v.ganVao) if (!coTrang(p)) loi.push(`Không có trang ${p} trên site.`);
  return loi;
}

/** Gộp: bản Firestore thay trọn bản trong code cùng mã; chỉ giữ bản đã xuất bản. Mới đăng trước. */
export function gopVideo(trongCode = [], tuFirestore = []) {
  const m = new Map();
  for (const v of trongCode) { const c = chuanHoaVideo(v); if (c.id) m.set(c.id, c); }
  for (const v of tuFirestore) { const c = chuanHoaVideo(v); if (c.id) m.set(c.id, c); }
  return [...m.values()].filter((v) => v.status === 'PUBLISHED')
    .sort((a, b) => (b.ngayDang || '').localeCompare(a.ngayDang || '') || a.tieuDe.localeCompare(b.tieuDe));
}

export function videoChoTrang(ds, path) {
  const p = chuanHoaDuongDan(path);
  return (ds || []).filter((v) => v.ganVao.includes(p));
}

export function loaiTrang(path) {
  if (path.startsWith('/thuat-ngu/')) return 'tu-dien';
  if (path.startsWith('/playbook/')) return 'playbook';
  if (path.startsWith('/cong-cu/')) return 'cong-cu';
  return 'khac';
}

/** Các loại trang một video được gắn vào (dùng cho bộ lọc). */
export function loaiCuaVideo(v) {
  const ds = [...new Set(v.ganVao.map(loaiTrang))];
  return ds.length ? ds : ['khac'];
}

/**
 * Ảnh bìa cho thẻ: hq720 (1280x720) có khung dọc của Shorts nằm giữa, cắt giữa
 * là vừa khung 9:16. Bản dọc oardefault thường chưa có (YouTube trả ảnh xám
 * 120x90), đo ngày 27/10/2026 cả 6 video đầu đều vậy, nên không dùng.
 * hqdefault luôn có, dùng làm dự phòng và khai cho Google.
 */
export const anhBiaDoc = (id) => `https://i.ytimg.com/vi/${id}/hq720.jpg`;
export const anhBia = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
export const linkXem = (id) => `https://www.youtube.com/shorts/${id}`;
/** Chế độ youtube-nocookie: YouTube không đặt cookie cho tới khi người xem bấm phát. */
export const linkNhung = (id, tuPhat = true) =>
  `https://www.youtube-nocookie.com/embed/${id}?${tuPhat ? 'autoplay=1&' : ''}playsinline=1&rel=0`;

export function thoiLuongISO(sec) {
  if (!sec) return '';
  const p = Math.floor(sec / 60), g = sec % 60;
  return `PT${p ? `${p}M` : ''}${g || !p ? `${g}S` : ''}`;
}

export function thoiLuongChu(sec) {
  if (!sec) return '';
  const p = Math.floor(sec / 60), g = sec % 60;
  return p ? `${p}:${String(g).padStart(2, '0')}` : `${g} giây`;
}

/** Dữ liệu có cấu trúc VideoObject (schema.org) cho trang chứa video. */
export function videoObject(v, host = '') {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: v.tieuDe,
    description: v.moTa || v.tieuDe,
    thumbnailUrl: [anhBia(v.id)],
    uploadDate: v.ngayDang,
    ...(v.thoiLuong ? { duration: thoiLuongISO(v.thoiLuong) } : {}),
    embedUrl: `https://www.youtube.com/embed/${v.id}`,
    contentUrl: linkXem(v.id),
    inLanguage: 'vi-VN',
    ...(v.loiThoai ? { transcript: v.loiThoai } : {}),
    publisher: { '@type': 'Organization', name: 'fundraising.vn', url: host || 'https://fundraising.vn' },
  };
}
